import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi, type CreateUserRequest } from '@/api/users.api';
import { rolesApi } from '@/api/roles.api';
import { masterDataApi } from '@/api/masterData.api';
import { auditsApi, type AuditLogDto, type PagedResult, type SecurityAuditLogDto } from '@/api/audits.api';
import { notificationsApi } from '@/api/notifications.api';

export const adminKeys = {
  users: ['admin', 'users'] as const,
  roles: ['admin', 'roles'] as const,
  settings: ['admin', 'app-settings'] as const,
  audit: (kind: 'business' | 'security', page: number) => ['admin', 'audit', kind, page] as const,
  notifications: (page: number) => ['notifications', page] as const,
};

export const useUsersQuery = () => useQuery({ queryKey: adminKeys.users, queryFn: usersApi.list });
export const useRolesQuery = () => useQuery({ queryKey: adminKeys.roles, queryFn: rolesApi.list });
export const useAppSettingsQuery = () => useQuery({ queryKey: adminKeys.settings, queryFn: masterDataApi.appSettings });
type AuditEntry = AuditLogDto | SecurityAuditLogDto;
export const useAuditLogsQuery = (kind: 'business' | 'security', page = 1) => useQuery<PagedResult<AuditEntry>>({
  queryKey: adminKeys.audit(kind, page),
  queryFn: async () => kind === 'business' ? auditsApi.business({ page, pageSize: 20 }) : auditsApi.security({ page, pageSize: 20 }),
});
export const useNotificationsQuery = (page = 1) => useQuery({ queryKey: adminKeys.notifications(page), queryFn: () => notificationsApi.list({ page, pageSize: 20 }) });

export function useCreateUser() {
  const client = useQueryClient();
  return useMutation({ mutationFn: (body: CreateUserRequest) => usersApi.create(body), onSuccess: () => client.invalidateQueries({ queryKey: adminKeys.users }) });
}

export function useUpdateAppSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value, description }: { key: string; value: string; description: string }) => masterDataApi.updateAppSetting(key, { value, description }),
    onSuccess: () => client.invalidateQueries({ queryKey: adminKeys.settings }),
  });
}
